import { commonComponents as cc } from "@/components/common";
import SharedAttributeContactPerson from "@/features/shared_attributes/components/SharedAttributeContactPerson";
import SharedAttributeDatePicker from "@/features/shared_attributes/components/SharedAttributeDatePicker";
import SharedAttributeHospitalNumberForm from "@/features/shared_attributes/components/SharedAttributeHospitalNumberForm";
import type { SharedAttributeJSON } from "@/features/shared_attributes/types";
import { services as s } from "@/services";
import type { StatefulDiaryT } from "@/types/database/diaries";
import type { StatefulTrackerViewT } from "@/types/database/trackers";
import { useAuth } from "@/utils/auth";
import { useOrganisationCliniciansQuery } from "../../users/api/getClinicians";

const SharedAttributes = ({
	object,
	organisationId,
}: {
	object: StatefulDiaryT | StatefulTrackerViewT;
	organisationId: string;
}) => {
	const auth = useAuth();
	const { data: clinicians } = useOrganisationCliniciansQuery(organisationId);
	const showContact =
		object.shared_attributes?.contact_id || !auth.roles?.isPatientOnly;
	return (
		<>
			<cc.EditDialog
				label="Hospital Number"
				description="Hospital reference number for all trackers."
				info={object?.shared_attributes?.hospital_number}
				noInfo={!object?.shared_attributes?.hospital_number}
			>
				<SharedAttributeHospitalNumberForm
					sharedAttributes={object?.shared_attributes}
					initialHospitalNumber={
						object?.shared_attributes?.hospital_number || undefined
					}
				/>
			</cc.EditDialog>
			<cc.EditDialog
				label="Start Date"
				info={s.time.formatShortDateTime(object?.shared_attributes?.start_date)}
				noInfo={!object?.shared_attributes?.start_date}
			>
				<SharedAttributeDatePicker
					dateType="start_date"
					sharedAttributes={object?.shared_attributes}
				/>
			</cc.EditDialog>

			<cc.EditDialog
				label="Reference Date"
				description="The date of the injection, surgery or other intervention."
				info={s.time.formatShortDateTime(
					object?.shared_attributes?.reference_date,
				)}
				noInfo={!object?.shared_attributes?.reference_date}
			>
				<SharedAttributeDatePicker
					dateType="reference_date"
					sharedAttributes={object?.shared_attributes}
				/>
			</cc.EditDialog>

			{object?.shared_attributes.end_date && (
				<cc.EditDialog
					label="Reference Date"
					description="The reference event date for all trackers."
					info={s.time.formatShortDateTime(object?.shared_attributes?.end_date)}
					noInfo={!object?.shared_attributes?.end_date}
				>
					<SharedAttributeDatePicker
						dateType="end_date"
						sharedAttributes={object?.shared_attributes}
					/>
				</cc.EditDialog>
			)}

			{showContact && (
				<cc.EditDialog
					label="Contact Person"
					description="Primary contact for all trackers in this object?."
					info={
						clinicians?.find(
							(c) => c.user_id === object?.shared_attributes?.contact_id,
						)?.email ?? "N/A"
					}
					noInfo={!object?.shared_attributes?.contact_id}
					locked={!auth.roles?.isClinician}
				>
					<SharedAttributeContactPerson
						sharedAttributes={object?.shared_attributes as SharedAttributeJSON}
						initialContactId={
							object?.shared_attributes?.contact_id || undefined
						}
						organisationId={organisationId}
					/>
				</cc.EditDialog>
			)}
		</>
	);
};

export default SharedAttributes;
